// The kind registry: screens that depend on a spec kind look their presenter up here, never in
// an if/else chain (platform ADR 0012). The 16 kinds are registered from kinds/presenters.ts.
import { KINDS, type Kind, type KindSlug } from "@cosmosai/specs/kinds";
import { BoxIcon, type LucideIcon } from "lucide-react";
import { type KindGroup, type KindRow, kindTable, type Placement } from "./presenters";

export type { KindGroup, Placement } from "./presenters";

export interface KindPresenter {
  /** The kind as written in a spec, for example "Agent". */
  kind: string;
  /** The kind in references and URLs, for example "agent". */
  slug: string;
  /** i18n keys of the singular and the plural label. */
  labelKey: string;
  pluralKey: string;
  icon: LucideIcon;
  group: KindGroup;
  placement: Placement;
  order: number;
}

const presenters = new Map<string, KindPresenter>();

export function registerKind(presenter: KindPresenter): void {
  if (presenters.has(presenter.kind)) throw new Error(`kind already registered: ${presenter.kind}`);
  presenters.set(presenter.kind, presenter);
}

/** Factory: a presenter from a kind of the contract and its row of the table. */
const presenterFrom = (slug: KindSlug, kind: Kind, row: KindRow): KindPresenter => ({
  kind,
  slug,
  labelKey: `kinds.${slug}.one`,
  pluralKey: `kinds.${slug}.other`,
  ...row,
});

for (const [slug, kind] of Object.entries(KINDS) as Array<[KindSlug, Kind]>) {
  registerKind(presenterFrom(slug, kind, kindTable[kind]));
}

/** The presenter of a kind, or a generic one for kinds the contract does not know yet. */
export function presenterOf(kind: string): KindPresenter {
  return (
    presenters.get(kind) ?? {
      kind,
      slug: kind.toLowerCase(),
      labelKey: "kinds.unknown.one",
      pluralKey: "kinds.unknown.other",
      icon: BoxIcon,
      group: "build",
      placement: "project",
      order: 1000,
    }
  );
}

/** The presenter of a kind slug, such as "knowledge-base". */
export function presenterOfSlug(slug: string): KindPresenter {
  return presenterOf(KINDS[slug as KindSlug] ?? slug);
}

/** Registered kinds, by group then order. */
export const registeredKinds = (): KindPresenter[] =>
  [...presenters.values()].sort((a, b) => a.group.localeCompare(b.group) || a.order - b.order);

/** The kinds of a group, in their order. */
export const kindsIn = (group: KindGroup): KindPresenter[] =>
  registeredKinds().filter((presenter) => presenter.group === group);
